"use client";

import { useEffect, useState } from "react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { Menu, X } from "lucide-react";
function HeaderMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const uniqueMenuItems = menuItems.reduce((acc, item) => {
    const exists = acc.find(existing => existing.label === item.label);
    if (!exists) {
      acc.push(item);
    }
    return acc;
  }, [] as typeof menuItems);
  const filteredMenuItems = uniqueMenuItems.filter(item => !item.label.includes("Supplier and Buyer Portal Suite") && !item.label.includes("Supplier Portal and Buyer Portal"));
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [mobileMenuOpen]);
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape" && mobileMenuOpen) {
        setMobileMenuOpen(false);
      }
    };
    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [mobileMenuOpen]);
  if (loading) {
    return <div className="flex gap-1">
        <div className="h-9 w-20 bg-muted/20 animate-pulse rounded-sm" />
        <div className="h-9 w-20 bg-muted/20 animate-pulse rounded-sm" />
        <div className="h-9 w-20 bg-muted/20 animate-pulse rounded-sm" />
      </div>;
  }
  return <>
      {}
      <NavigationMenu viewport={false} className="hidden lg:flex">
        <NavigationMenuList>
          {filteredMenuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
              <NavigationMenuLink asChild className="hover:!bg-transparent focus:!bg-transparent relative group px-4 py-2 text-[15px] font-medium text-foreground transition-colors duration-150 whitespace-nowrap">
                <Link to={item.href || "#"}>
                  {item.label}
                  <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-accent transition-all duration-150 group-hover:w-full" />
                </Link>
              </NavigationMenuLink>
            </NavigationMenuItem>)}
        </NavigationMenuList>
      </NavigationMenu>

      {}
      <Button variant="ghost" size="icon" className="lg:hidden hover:!bg-transparent focus:!bg-transparent" onClick={() => setMobileMenuOpen(!mobileMenuOpen)} aria-label="Toggle menu" aria-expanded={mobileMenuOpen}>
        <Menu className="size-6" />
      </Button>

      {}
      {mobileMenuOpen && <>
          {}
          <div className="mobile-menu-overlay fixed inset-0 w-full h-full bg-background/60 backdrop-blur-sm lg:hidden animate-fade-in pointer-events-auto" style={{
        zIndex: 9998,
        top: 0,
        left: 0
      }} onClick={() => setMobileMenuOpen(false)} aria-hidden="true" />

          {}
          <div className="fixed top-0 right-0 h-screen w-full max-w-[320px] bg-background shadow-strong lg:hidden animate-slide-in-from-right pointer-events-auto" style={{
        zIndex: 9999
      }} role="dialog" aria-modal="true" aria-label="Mobile navigation menu">
            <div className="flex flex-col h-full">
              {}
              <div className="flex items-center justify-between p-4 border-b border-border/50 bg-background">
                <img src="https://globalwholesaletrade.com/wp-content/uploads/2026/02/GWTlogo002-Transparent-1.png" alt="Global Wholesale Trade" className="h-20" />
                <Button variant="ghost" size="icon" className="hover:!bg-transparent focus:!bg-transparent" onClick={() => setMobileMenuOpen(false)} aria-label="Close menu">
                  <X className="size-6" />
                </Button>
              </div>

              {}
              <nav className="flex-1 overflow-y-auto p-6">
                <div className="flex flex-col gap-2">
                  {filteredMenuItems.map((item, i) => <Link key={item.id} data-index={i} to={item.href || "#"} className="px-4 py-3 text-[15px] font-medium text-foreground hover:bg-accent/10 rounded-sm transition-colors duration-150 border border-transparent hover:border-accent/20 whitespace-nowrap" onClick={() => setMobileMenuOpen(false)}>
                      {item.label}
                    </Link>)}
                </div>
              </nav>

              {}
              <div className="p-6 border-t border-border/50 bg-background">
                <Button className="w-full hover:!bg-transparent focus:!bg-transparent border-2 border-accent-dark bg-transparent text-accent-dark hover:border-accent-hover hover:text-accent-dark transition-all duration-120" onClick={() => setMobileMenuOpen(false)} asChild>
                  <Link to="#contact">Request a Quote</Link>
                </Button>
              </div>
            </div>
          </div>
        </>}
    </>;
}
export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (hashPart === "") {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: "smooth"
            });
            return;
          }
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <header id="header" className={`fixed top-0 left-0 right-0 z-40 bg-background/80 backdrop-blur-sm border-b border-border transition-all duration-150 ${scrolled ? "h-16 shadow-medium" : "h-[90px] shadow-soft"}`}>
      <div className="container mx-auto px-4 h-full flex items-center justify-between">
        <Link to="/" className="flex-shrink-0">
          <img src="https://globalwholesaletrade.com/wp-content/uploads/2026/02/GWTlogo002-Transparent-2.png" alt="Global Wholesale Trade" className="!w-[150px] !h-[150px]" />
        </Link>

        <div className="flex items-center gap-6">
          <MenuProvider menu_id="31" className="flex items-center">
            <HeaderMenuConsumer />
          </MenuProvider>

          <Button className="hidden lg:inline-flex hover:!bg-transparent focus:!bg-transparent border-2 border-accent-dark bg-transparent text-accent-dark hover:border-accent-hover hover:text-accent-dark transition-all duration-120" asChild>
            <Link to="#contact">Request a Quote</Link>
          </Button>
        </div>
      </div>
    </header>;
}